import React, { useState, useRef } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop, convertToPixelCrop } from 'react-image-crop';
import { canvasPreview } from './canvasPreview';
import { useDebounceEffect } from './useDebounceEffect';
import { Box, Button, Input, FormLabel, Text, Slider, SliderTrack, SliderFilledTrack, SliderThumb } from '@chakra-ui/react';
import 'react-image-crop/dist/ReactCrop.css';

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export default function AutoCrop() {
  const [imgSrc, setImgSrc] = useState('');
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const hiddenAnchorRef = useRef<HTMLAnchorElement>(null);
  const blobUrlRef = useRef('');
  const [crop, setCrop] = useState<Crop | undefined>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  const [fileTypeError, setFileTypeError] = useState<string>('');

  // File selection handler
  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const fileType = file.type.split('/')[0];

      if (fileType !== 'image') {
        setFileTypeError('Please select a valid image file.');
        return;
      } else {
        setFileTypeError('');
      }

      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(file);
    }
  }

  // Image load handler
  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (imgRef.current) {
      setCrop({
        unit: '%',
        width: 100,
        height: 100,
        x: 0,
        y: 0,
      });
    }
  }

  // Download crop handler
  async function onDownloadCropClick() {
    const image = imgRef.current;
    if (!image || !completedCrop) {
      throw new Error('Image or completed crop not found');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Create an HTML canvas element
    const canvas = document.createElement('canvas');
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('No 2d context available');
    }

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Use toBlob to get the cropped image as a Blob
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          throw new Error('Failed to create blob');
        }

        const blobUrl = URL.createObjectURL(blob);

        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        blobUrlRef.current = blobUrl;

        // Trigger the download
        if (hiddenAnchorRef.current) {
          hiddenAnchorRef.current.href = blobUrl;
          hiddenAnchorRef.current.download = 'cropped-image.png'; // Set the download file name
          hiddenAnchorRef.current.click();
        }
      },
      'image/png',
      1 // Quality for the image
    );
  }

  // Debounce effect for image crop preview
  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate);
      }
    },
    100,
    [completedCrop, scale, rotate],
  );

  // Toggle aspect ratio
  function handleToggleAspectClick() {
    if (aspect) {
      setAspect(undefined);
    } else {
      setAspect(16 / 9);

      if (imgRef.current) {
        const { width, height } = imgRef.current;
        const newCrop = centerAspectCrop(width, height, 16 / 9);
        setCrop(newCrop);
        setCompletedCrop(convertToPixelCrop(newCrop, width, height));
      }
    }
  }

  // Reset crop settings
  function handleResetClick() {
    setScale(1);
    setRotate(0);
    setAspect(undefined);
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      const newCrop: Crop = {
        unit: '%',
        width: 100,
        height: 100,
        x: 0,
        y: 0,
      };
      setCrop(newCrop);
      setCompletedCrop(convertToPixelCrop(newCrop, width, height));
    }
  }

  return (
    <Box p={6} maxWidth="800px" mx="auto">
      <Box mb={4}>
        <Input type="file" accept="image/*" onChange={onSelectFile} />
        {fileTypeError && <Text color="red.500">{fileTypeError}</Text>}
      </Box>

      <Box mb={4}>
        <FormLabel htmlFor="scale-input">Scale</FormLabel>
        <Slider
          id="scale-input"
          min={1}
          max={3}
          step={0.1}
          value={scale}
          onChange={setScale}
          isDisabled={!imgSrc}
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb />
        </Slider>
      </Box>

      <Box mb={4}>
        <FormLabel htmlFor="rotate-input">Rotate</FormLabel>
        <Slider
          id="rotate-input"
          min={-180}
          max={180}
          step={1}
          value={rotate}
          onChange={setRotate}
          isDisabled={!imgSrc}
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb />
        </Slider>
      </Box>

      <Box mb={4}>
        <Button onClick={handleToggleAspectClick} w="full" colorScheme={aspect ? 'teal' : 'gray'}>
          {aspect ? `Unset aspect ratio (16:9)` : `Set aspect ratio (16:9)`}
        </Button>
      </Box>

      <Box mb={4}>
        <Button onClick={handleResetClick} w="full" colorScheme="red">
          Reset Crop Settings
        </Button>
      </Box>

      {imgSrc && (
        <Box position="relative">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            minHeight={100}
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={imgSrc}
              style={{
                transform: `scale(${scale}) rotate(${rotate}deg)`,
                width: '100%',
                height: 'auto',
              }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        </Box>
      )}

      {completedCrop && (
        <>
          <Box mt={4}>
            <canvas
              ref={previewCanvasRef}
              style={{
                border: '1px solid black',
                objectFit: 'contain',
                width: completedCrop.width,
                height: completedCrop.height,
                margin: '0 auto',
              }}
            />
          </Box>

          <Box mt={4} textAlign="center">
            <Button onClick={onDownloadCropClick} colorScheme="teal">
              Download Cropped Image
            </Button>
          </Box>

          <a ref={hiddenAnchorRef} style={{ display: 'none' }} />
        </>
      )}
    </Box>
  );
}
